import type { ArticleSource } from "@general-applications/contracts/content";
import { httpArticleSource } from "@/app/features/blog/functions/articleApi";
import { BlogRouteError } from "@/app/pages/blogDetail/components/BlogRouteError";
import type { RouteObject } from "react-router-dom";

import { SiteLayout } from "@/app/common/components/composite/shell/SiteLayout";
import { routes } from "@/app/common/functions/routes/routes";

import { AboutPage } from "@/app/pages/about/AboutPage";
import { PrivacyPage } from "@/app/pages/privacy/PrivacyPage";
import { BlogPage } from "@/app/pages/blog/BlogPage";
import { BlogDetailPage } from "@/app/pages/blogDetail/BlogDetailPage";
import { CapabilitiesPage } from "@/app/pages/capabilities/CapabilitiesPage";
import { CapabilityDetailPage } from "@/app/pages/capabilityDetail/CapabilityDetailPage";
import { ContactPage } from "@/app/pages/contact/ContactPage";
import { HomePage } from "@/app/pages/home/HomePage";
import { IndustriesPage } from "@/app/pages/industries/IndustriesPage";
import { IndustryDetailPage } from "@/app/pages/industryDetail/IndustryDetailPage";
import { NotFoundPage } from "@/app/pages/notFound/NotFoundPage";
import { ServiceDetailPage } from "@/app/pages/serviceDetail/ServiceDetailPage";
import { ServicesPage } from "@/app/pages/services/ServicesPage";

// Router children use relative segments (no leading slash), while routes.ts
// stores absolute hrefs for <Link to>. Strip the slash so every path string
// still only lives in one place.
const relative = (href: string) => href.replace(/^\//, "");

// Pure route data, no side effects — kept separate from router.tsx's
// `createBrowserRouter` call (which touches `document` at module load time)
// so entry-server.tsx can import this for `createStaticHandler` without
// crashing under Node's SSR build.
export function createRouteObjects(
  source: ArticleSource = httpArticleSource,
): RouteObject[] {
  return [
    {
      path: "/",
      element: <SiteLayout />,
      children: [
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: relative(routes.about),
          element: <AboutPage />,
        },
        {
          path: relative(routes.contact),
          element: <ContactPage />,
        },
        {
          path: relative(routes.privacy),
          element: <PrivacyPage />,
        },
        {
          path: relative(routes.capabilities.index),
          element: <CapabilitiesPage />,
        },
        {
          path: `${relative(routes.capabilities.index)}/:slug`,
          element: <CapabilityDetailPage />,
        },
        {
          path: relative(routes.industries.index),
          element: <IndustriesPage />,
        },
        {
          path: `${relative(routes.industries.index)}/:slug`,
          element: <IndustryDetailPage />,
        },
        {
          path: relative(routes.services.index),
          element: <ServicesPage />,
        },
        {
          path: `${relative(routes.services.index)}/:slug`,
          element: <ServiceDetailPage />,
        },
        {
          path: relative(routes.blog.index),
          element: <BlogPage />,
        },
        {
          path: `${relative(routes.blog.index)}/:slug`,
          id: "blog-article",
          loader: async ({ params, request }) => {
            const post = params.slug
              ? await source.get(params.slug, request.signal)
              : null;
            if (!post) throw new Response("Not found", { status: 404 });
            return post;
          },
          errorElement: <BlogRouteError />,
          element: <BlogDetailPage />,
        },
        {
          path: relative(routes.notFound),
          element: <NotFoundPage />,
        },
        {
          path: "*",
          element: <NotFoundPage />,
        },
      ],
    },
  ];
}

export const routeObjects = createRouteObjects();
